import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';

import { cn } from '@shared/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none text-base font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-[hsl(var(--button-primary))] text-primary-foreground hover:bg-[hsl(var(--button-primary-hover))] focus-visible:ring-[hsl(var(--button-primary))]',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        destructiveGhost: 'text-destructive hover:bg-destructive/10 hover:text-destructive',
        outline: 'border border-primary bg-background text-primary hover:bg-primary/10',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-6 py-3',
        sm: 'h-10 px-4 text-sm',
        lg: 'h-12 px-8',
        icon: 'h-11 w-11',
      },
      density: {
        default: '',
        compact: 'lg:h-8 lg:text-sm',
      },
    },
    compoundVariants: [
      { density: 'compact', size: ['default', 'sm', 'lg'], className: 'lg:px-2' },
      { density: 'compact', size: 'sm', className: 'h-11' },
      { density: 'compact', size: 'icon', className: 'lg:w-8' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      density: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      density,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, density, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            {children}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
